/**
 * Voice Assistant Dialog
 *
 * A voice-powered AI assistant using OpenAI's Realtime Voice API
 * Voice only starts when user clicks "Start Conversation"
 */

import { useState, useEffect, useRef, useCallback, type FC } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  alpha,
  CircularProgress,
  Stack,
  Button,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StopIcon from '@mui/icons-material/Stop';
import { executeTool, toolDefinitions } from './agent-tools';
import { getCompanyInfo } from 'src/api/supabase/company-settings';

// Convert tool definitions to OpenAI Realtime API format
const getRealtimeTools = () => {
  // Only include a subset of tools that make sense for voice
  const voiceToolNames = [
    'create_customer',
    'search_customer',
    'create_quote',
    'get_recent_leads',
    'send_email',
    'send_text_message',
  ];

  return toolDefinitions
    .filter(tool => voiceToolNames.includes(tool.name))
    .map(tool => ({
      type: 'function' as const,
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters
    }));
};

// Animations
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const breathe = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(41, 112, 255, 0.4); }
  50% { transform: scale(1.02); box-shadow: 0 0 0 20px rgba(41, 112, 255, 0); }
`;

const audioWave = keyframes`
  0%, 100% { height: 8px; }
  50% { height: 24px; }
`;

const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(2.5); opacity: 0; }
`;

const micPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  50% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
`;

const robotSpin = keyframes`
  0% { transform: rotateY(0deg) scale(1); }
  25% { transform: rotateY(180deg) scale(1.1); }
  50% { transform: rotateY(360deg) scale(1); }
  75% { transform: rotateY(540deg) scale(1.1); }
  100% { transform: rotateY(720deg) scale(1); }
`;

// Aware Logo Icon Component
const AwareIcon: FC<{ size?: number; animate?: boolean; transparent?: boolean; white?: boolean }> = ({ size = 40, animate = false, transparent = false, white = false }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: '50%',
      backgroundColor: transparent ? 'transparent' : '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      animation: animate ? `${robotSpin} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards` : 'none',
    }}
  >
    <Box
      component="img"
      src="/aware.png"
      alt="Aware"
      sx={{
        width: size,
        height: size,
        filter: white ? 'brightness(0) invert(1)' : (transparent ? 'none' : 'brightness(0)'),
        objectFit: 'cover',
      }}
    />
  </Box>
);

interface VoiceAssistantDialogProps {
  open: boolean;
  onClose: () => void;
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
type AssistantState = 'idle' | 'listening' | 'thinking' | 'speaking';

export const VoiceAssistantDialog: FC<VoiceAssistantDialogProps> = ({ open, onClose }) => {
  // Voice assistant state
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [assistantState, setAssistantState] = useState<AssistantState>('idle');
  const [voiceTranscript, setVoiceTranscript] = useState<string>('');
  const [voiceResponse, setVoiceResponse] = useState<string>('');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  // Refs for WebRTC and audio
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Get OpenAI API key from localStorage (user must provide it)
  // Note: The key is NOT stored in env vars for security - it's user-provided
  const getOpenAIApiKey = useCallback(() => {
    return localStorage.getItem('relight_openai_api_key') || '';
  }, []);

  // Initialize audio level monitoring
  const startAudioMonitoring = useCallback((stream: MediaStream) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }

    const source = audioContextRef.current.createMediaStreamSource(stream);
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 256;
    source.connect(analyserRef.current);

    const updateLevel = () => {
      if (analyserRef.current && assistantState === 'listening') {
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(average / 255);
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      }
    };
    updateLevel();
  }, [assistantState]);

  // Connect to OpenAI Realtime API - ONLY called when user clicks "Start Conversation"
  const connectToRealtime = useCallback(async () => {
    const openaiKey = getOpenAIApiKey();
    if (!openaiKey) {
      setVoiceError('OpenAI API key not configured. Add REACT_APP_OPENAI_API_KEY to your .env file.');
      setConnectionStatus('error');
      return;
    }

    setConnectionStatus('connecting');
    setVoiceError(null);

    try {
      const tokenResponse = await fetch('https://api.openai.com/v1/realtime/sessions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openaiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-realtime-preview-2024-12-17',
          voice: 'alloy',
        }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to create session: ${tokenResponse.status}`);
      }

      const sessionData = await tokenResponse.json();
      const ephemeralKey = sessionData.client_secret?.value;

      if (!ephemeralKey) {
        throw new Error('No ephemeral key received from OpenAI');
      }

      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      // Create audio element and append to DOM for playback
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioEl.id = 'voice-assistant-audio';
      // Remove any existing audio element
      const existingAudio = document.getElementById('voice-assistant-audio');
      if (existingAudio) existingAudio.remove();
      document.body.appendChild(audioEl);

      pc.ontrack = (e) => {
        // console.log('Received audio track from OpenAI');
        audioEl.srcObject = e.streams[0];
        // Ensure audio plays
        audioEl.play().catch(() => { /* Audio play error silenced for production */ });
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      audioStreamRef.current = stream;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      startAudioMonitoring(stream);

      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;

      dc.onopen = () => {
        // console.log('Data channel opened, configuring session...');

        // Configure session with tools for function calling
        dc.send(JSON.stringify({
          type: 'session.update',
          session: {
            modalities: ['text', 'audio'],
            instructions: `You are Aware, a friendly and efficient voice assistant for ${getCompanyInfo().companyName || 'Relight'}'s analytics dashboard. ${getCompanyInfo().fullCompanyName || 'Relight Exterior Lighting'} is an outdoor permanent LED lighting company.

PERSONALITY:
- Warm, professional, and conversational
- Speak naturally like a helpful colleague, not a robot
- Keep responses concise but friendly
- Use a casual yet professional tone

YOU HAVE TOOLS TO EXECUTE ACTIONS:
- create_customer: Create a new lead/customer with their name, footage, and contact info
- search_customer: Look up existing customers by name, email, or phone
- create_quote: Generate a quote for a customer (price is $28/ft + $350 controller)
- get_recent_leads: Get a list of recent leads
- send_email: Send an email to a customer
- send_text_message: Send a text message to a customer

CONVERSATION FLOW:
1. When the user gives a command, ALWAYS confirm what you understood
2. If anything is unclear, ask ONE clarifying question at a time (e.g., "What's the customer's last name?")
3. Once you have all required info, USE THE TOOLS to execute the action
4. After the tool executes, confirm completion: "Done! I've [what you did]. Anything else?"
5. Keep the conversation flowing naturally

EXAMPLE CONVERSATION:
- User: "Create a lead for Zach Miller for 150 feet"
- You: "Got it! Creating a lead for Zach Miller with 150 feet." [call create_customer tool]
- [After tool completes]: "Done! I've created the lead for Zach Miller - 150 feet. Would you like me to create a quote for him too?"

IMPORTANT:
- When you have enough info, CALL THE TOOLS - don't just say you'll do it
- Always use tools to actually execute actions
- Report back the result after tools complete
- If a tool fails, explain what went wrong`,
            voice: 'alloy',
            input_audio_transcription: {
              model: 'whisper-1',
            },
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 1000,
            },
            tools: getRealtimeTools(),
            tool_choice: 'auto',
          },
        }));

        // console.log('Session configuration sent');

        // Small delay to ensure session is configured before requesting response
        setTimeout(() => {
          // console.log('Sending initial greeting request...');
          // Send initial greeting - use conversation.item.create + response.create pattern
          dc.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
              type: 'message',
              role: 'user',
              content: [{
                type: 'input_text',
                text: 'Hello, I just started the voice assistant. Please greet me warmly.'
              }]
            }
          }));

          // Then request a response with explicit modalities
          dc.send(JSON.stringify({
            type: 'response.create',
            response: {
              modalities: ['text', 'audio']
            }
          }));
        }, 500);
      };

      dc.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          handleRealtimeEvent(event);
        } catch (err) {
          // console.error('Failed to parse realtime event:', err);
        }
      };

      dc.onerror = (err) => {
        // console.error('Data channel error:', err);
        setVoiceError('Connection error occurred');
      };

      dc.onclose = () => {
        // console.log('Data channel closed');
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResponse = await fetch(`https://api.openai.com/v1/realtime?model=gpt-4o-realtime-preview-2024-12-17`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
      });

      if (!sdpResponse.ok) {
        throw new Error(`SDP exchange failed: ${sdpResponse.status}`);
      }

      const answerSdp = await sdpResponse.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      setConnectionStatus('connected');
      setAssistantState('listening');

    } catch (err) {
      // console.error('Failed to connect to OpenAI Realtime:', err);
      setVoiceError(err instanceof Error ? err.message : 'Failed to connect');
      setConnectionStatus('error');
      cleanup();
    }
  }, [getOpenAIApiKey, startAudioMonitoring]);

  // Handle function call execution
  const handleFunctionCall = useCallback(async (callId: string, name: string, args: Record<string, unknown>) => {
    // console.log('Executing function:', name, args);
    setAssistantState('thinking');

    try {
      const result = await executeTool(name, args);
      // console.log('Function result:', result);

      // Send the result back to the API
      if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
        // Send function call output
        dataChannelRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify(result)
          }
        }));

        // Request a response after the function call
        const resultData = result.data as { message?: string } | undefined;
        dataChannelRef.current.send(JSON.stringify({
          type: 'response.create',
          response: {
            modalities: ['text', 'audio'],
            instructions: result.success
              ? `The action was successful. Tell the user: ${resultData?.message || 'Done!'} Then ask if they need anything else.`
              : `The action failed with error: ${result.error}. Apologize and ask if they want to try again.`
          }
        }));
      }
    } catch (error) {
      // console.error('Function call error:', error);
      if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
        dataChannelRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify({ success: false, error: error instanceof Error ? error.message : 'Unknown error' })
          }
        }));

        dataChannelRef.current.send(JSON.stringify({
          type: 'response.create',
          response: {
            modalities: ['text', 'audio'],
            instructions: `There was an error: ${error instanceof Error ? error.message : 'Unknown error'}. Apologize to the user and ask if they want to try again.`
          }
        }));
      }
    }
  }, []);

  // Handle events from OpenAI Realtime API
  const handleRealtimeEvent = useCallback((event: any) => {
    // console.log('Realtime event:', event.type, event);

    switch (event.type) {
      case 'session.created':
        // console.log('Session created successfully:', event.session);
        break;
      case 'session.updated':
        // console.log('Session updated:', {
        //   tools: event.session?.tools?.length || 0,
        //   modalities: event.session?.modalities,
        //   voice: event.session?.voice
        // });
        break;
      case 'response.created':
        // console.log('Response created:', event.response?.id, 'modalities:', event.response?.modalities);
        break;
      case 'input_audio_buffer.speech_started':
        setAssistantState('listening');
        break;
      case 'input_audio_buffer.speech_stopped':
        setAssistantState('thinking');
        break;
      case 'conversation.item.input_audio_transcription.completed':
        setVoiceTranscript(event.transcript || '');
        break;
      case 'response.output_item.added':
        // console.log('Output item added:', event.item?.type, event.item?.id);
        break;
      case 'response.content_part.added':
        // console.log('Content part added:', event.part?.type);
        break;
      case 'response.audio_transcript.delta':
        // console.log('Audio transcript delta:', event.delta);
        setVoiceResponse(prev => prev + (event.delta || ''));
        break;
      case 'response.audio_transcript.done':
        // console.log('Audio transcript done');
        break;
      case 'response.audio.delta':
        // console.log('Audio delta received, length:', event.delta?.length || 0);
        setAssistantState('speaking');
        break;
      case 'response.audio.done':
        setAssistantState('listening');
        setVoiceResponse('');
        setVoiceTranscript('');
        break;
      case 'response.function_call_arguments.done':
        // Function call requested by the model
        // console.log('Function call requested:', event);
        if (event.call_id && event.name && event.arguments) {
          try {
            const args = JSON.parse(event.arguments);
            handleFunctionCall(event.call_id, event.name, args);
          } catch (e) {
            // console.error('Failed to parse function arguments:', e);
          }
        }
        break;
      case 'response.output_item.done':
        // Check if this is a function call item
        if (event.item?.type === 'function_call') {
          // console.log('Function call item done:', event.item);
          const { call_id, name, arguments: argsString } = event.item;
          if (call_id && name && argsString) {
            try {
              const args = JSON.parse(argsString);
              handleFunctionCall(call_id, name, args);
            } catch (e) {
              // console.error('Failed to parse function arguments:', e);
            }
          }
        }
        break;
      case 'response.done':
        // console.log('Response done:', {
        //   id: event.response?.id,
        //   status: event.response?.status,
        //   status_details: event.response?.status_details,
        //   output: event.response?.output?.map((o: any) => ({ type: o.type, id: o.id })),
        //   usage: event.response?.usage
        // });
        // Check if response contains function calls
        if (event.response?.output) {
          const functionCalls = event.response.output.filter((item: any) => item.type === 'function_call');
          if (functionCalls.length === 0) {
            setAssistantState('listening');
          }
        } else {
          setAssistantState('listening');
        }
        break;
      case 'error':
        // console.error('Realtime API error:', event.error);
        setVoiceError(event.error?.message || 'An error occurred');
        break;
    }
  }, [handleFunctionCall]);

  // Cleanup function
  const cleanup = useCallback(() => {
    // console.log('Cleaning up voice assistant...');
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    // Remove the audio element
    const audioEl = document.getElementById('voice-assistant-audio');
    if (audioEl) audioEl.remove();

    setConnectionStatus('disconnected');
    setAssistantState('idle');
    setVoiceTranscript('');
    setVoiceResponse('');
    setAudioLevel(0);
  }, []);

  const stopConversation = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // Cleanup when dialog closes - but DO NOT auto-connect when dialog opens
  useEffect(() => {
    if (!open) {
      cleanup();
    }
  }, [open, cleanup]);

  // Handle close
  const handleClose = useCallback(() => {
    cleanup();
    onClose();
  }, [cleanup, onClose]);

  // Get status text
  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connecting':
        return 'Connecting...';
      case 'error':
        return 'Connection failed';
      case 'connected':
        switch (assistantState) {
          case 'listening':
            return "I'm listening...";
          case 'thinking':
            return 'Processing...';
          case 'speaking':
            return 'Speaking...';
          default:
            return 'Ready to chat';
        }
      default:
        return 'Tap to start talking';
    }
  };

  // Get status color
  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connecting':
        return '#F59E0B';
      case 'error':
        return '#EF4444';
      case 'connected':
        return '#10B981';
      default:
        return alpha('#fff', 0.5);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0a0a0a',
          backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(41, 112, 255, 0.15) 0%, transparent 50%)',
          border: '1px solid',
          borderColor: alpha('#fff', 0.1),
          borderRadius: 4,
          overflow: 'hidden',
          minHeight: 600,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: alpha('#fff', 0.08),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(41,112,255,0.12) 0%, rgba(41,112,255,0.04) 100%)',
              border: '1px solid rgba(41,112,255,0.15)',
            }}
          >
            <AwareIcon size={28} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, fontSize: '1rem' }}>
              Aware AI
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: getStatusColor(),
                  boxShadow: `0 0 8px ${getStatusColor()}`
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: getStatusColor(),
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                {connectionStatus === 'connected' ? 'Connected' : 'Ready'}
              </Typography>
            </Stack>
          </Box>
        </Stack>
        <IconButton onClick={handleClose} sx={{ color: alpha('#fff', 0.5) }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Voice Content */}
      {(
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            px: 4,
            gap: 3,
            flex: 1,
          }}
        >
          {/* Robot Icon with Animation */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {assistantState === 'listening' && connectionStatus === 'connected' && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    width: 160,
                    height: 160,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: alpha('#2970FF', 0.3),
                    animation: `${ripple} 2s ease-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    width: 160,
                    height: 160,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: alpha('#2970FF', 0.3),
                    animation: `${ripple} 2s ease-out infinite 0.5s`,
                  }}
                />
              </>
            )}

            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: alpha('#2970FF', 0.1),
                border: '3px solid',
                borderColor: connectionStatus === 'connected'
                  ? alpha('#2970FF', 0.5)
                  : alpha('#fff', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: assistantState === 'speaking'
                  ? `${pulse} 1s ease-in-out infinite`
                  : assistantState === 'listening'
                  ? `${breathe} 2s ease-in-out infinite`
                  : 'none',
                transition: 'all 0.3s ease',
                boxShadow: connectionStatus === 'connected'
                  ? `0 0 40px ${alpha('#2970FF', 0.3)}`
                  : 'none',
              }}
            >
              {connectionStatus === 'connecting' ? (
                <CircularProgress sx={{ color: '#2970FF' }} size={48} />
              ) : (
                <SmartToyIcon
                  sx={{
                    fontSize: 56,
                    color: connectionStatus === 'connected' ? '#2970FF' : alpha('#fff', 0.3),
                    transition: 'color 0.3s ease',
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Audio Level Visualization */}
          {assistantState === 'listening' && connectionStatus === 'connected' && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: 32 }}>
              {[...Array(5)].map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 4,
                    height: 8 + (audioLevel * 24 * Math.random()),
                    bgcolor: '#2970FF',
                    borderRadius: 2,
                    animation: `${audioWave} ${0.5 + i * 0.1}s ease-in-out infinite`,
                    animationDelay: `${i * 0.1}s`,
                  }}
                />
              ))}
            </Stack>
          )}

          {/* Status Text */}
          <Typography
            variant="h5"
            sx={{
              color: '#fff',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {getStatusText()}
          </Typography>

          {/* Transcript/Response Display */}
          {(voiceTranscript || voiceResponse) && (
            <Box
              sx={{
                width: '100%',
                p: 3,
                borderRadius: 2,
                bgcolor: alpha('#fff', 0.03),
                border: '1px solid',
                borderColor: alpha('#fff', 0.06),
              }}
            >
              {voiceTranscript && (
                <Typography
                  sx={{
                    color: alpha('#fff', 0.7),
                    fontSize: '0.875rem',
                    mb: voiceResponse ? 2 : 0,
                  }}
                >
                  <strong style={{ color: '#2970FF' }}>You:</strong> {voiceTranscript}
                </Typography>
              )}
              {voiceResponse && (
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: '0.875rem',
                  }}
                >
                  <strong style={{ color: '#10B981' }}>Assistant:</strong> {voiceResponse}
                </Typography>
              )}
            </Box>
          )}

          {/* Error Display */}
          {voiceError && (
            <Box
              sx={{
                width: '100%',
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#EF4444', 0.1),
                border: '1px solid',
                borderColor: alpha('#EF4444', 0.3),
              }}
            >
              <Typography sx={{ color: '#EF4444', fontSize: '0.875rem' }}>
                {voiceError}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={2}>
            {connectionStatus === 'disconnected' || connectionStatus === 'error' ? (
              <Button
                variant="contained"
                startIcon={<MicIcon />}
                onClick={connectToRealtime}
                sx={{
                  bgcolor: '#2970FF',
                  '&:hover': { bgcolor: '#1D5BD6' },
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                }}
              >
                Start Conversation
              </Button>
            ) : connectionStatus === 'connected' ? (
              <Button
                variant="outlined"
                startIcon={<StopIcon />}
                onClick={stopConversation}
                sx={{
                  borderColor: '#EF4444',
                  color: '#EF4444',
                  '&:hover': {
                    borderColor: '#EF4444',
                    bgcolor: alpha('#EF4444', 0.1),
                  },
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                }}
              >
                End Conversation
              </Button>
            ) : null}
          </Stack>

          {/* Helper Text */}
          <Typography
            sx={{
              color: alpha('#fff', 0.4),
              fontSize: '0.75rem',
              textAlign: 'center',
              maxWidth: 340,
            }}
          >
            {connectionStatus === 'connected'
              ? 'Just speak naturally. Say things like "Create a lead for John for 200 feet" or "Send a quote to the last customer".'
              : 'Click to start a voice conversation. I\'ll help you create leads, send quotes, and manage your dashboard hands-free.'}
          </Typography>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default VoiceAssistantDialog;
